/**
 * AP-9 — Bus Statistics (FM-WIN-09).
 *
 * Zwei Kadenzen, wie im Original bewusst getrennt (CONTRACTS.md §3.4):
 * `refresh()` kommt aus dem 1-s-`stats`-Ereignis und schreibt Zusammenfassung
 * und Fehlerhistogramm, `appendBusLoadSample()` aus dem 250-ms-`bus-load`-
 * Ereignis und speist nur die Kurve.
 */

import { createCombo, createLabel } from '../ui/controls';
import { createDataTable } from './dataTable';
import { createFloatingPanel, type FloatingPanel } from './filters/FloatingPanel';
import { createChartCanvas, createRedrawScheduler, drawChartFrame } from './chartCanvas';
import { busStateColor } from '../ui/busStateColor';
import type { CanBusStatus } from '../can/ICanAdapter';
import './viewsC.css';

/** Combo-Einträge und ihre Sekundenwerte (BusStatsWindow.cpp:25). */
const TRACE_INTERVALS: ReadonlyArray<{ label: string; seconds: number }> = [
  { label: '5m', seconds: 300 },
  { label: '2m', seconds: 120 },
  { label: '1m', seconds: 60 },
  { label: '30s', seconds: 30 },
  { label: '15s', seconds: 15 },
  { label: '10s', seconds: 10 },
  { label: '5s', seconds: 5 },
];

const DEFAULT_WINDOW_SECONDS = 30;

/** Älteste vorgehaltene Probe; das größte Zeitfenster ist 5 Minuten. */
const SAMPLE_RETENTION_SECONDS = 300;

/** `BusLoadColorFor()` — feste Literale, keine Palettenwerte. */
function busLoadColor(percent: number): string {
  if (percent >= 90) return 'rgb(224,48,48)';
  if (percent >= 80) return 'rgb(224,138,32)';
  if (percent >= 40) return 'rgb(217,196,30)';
  return 'rgb(32,176,80)';
}

interface Sample {
  /** `performance.now()`-Zeitpunkt. */
  t: number;
  percent: number;
}

/** Die vom Fenster benötigten Felder von `StatsSnapshot`. */
export interface BusStatsData {
  rxFrameCount: number;
  busLoadPercent: number;
  busStatus: CanBusStatus;
  errorCategoryCounts: Record<string, number>;
}

export interface BusStatsHandle {
  panel: FloatingPanel;
  show(): void;
  /** Gegenstück zu `Refresh()` — im 1-s-Takt aufrufen. */
  refresh(data: BusStatsData): void;
  /** Gegenstück zu `AppendBusLoadSample()` — im 250-ms-Takt aufrufen. */
  appendBusLoadSample(percent: number): void;
  dispose(): void;
}

export function createBusStats(): BusStatsHandle {
  const panel = createFloatingPanel('Bus Statistics');
  panel.element.style.width = 'calc(460px * var(--ui-scale))';

  const view = document.createElement('div');
  view.className = 'cm-chart-view';

  const summary = document.createElement('div');
  summary.className = 'cm-chart-view__summary';
  // Nur der Zustand wird eingefärbt, der Rest bleibt Fließtext.
  const summaryHead = document.createElement('span');
  const summaryState = document.createElement('span');
  const summaryTail = document.createElement('span');
  summary.append(summaryHead, summaryState, summaryTail);

  const comboRow = document.createElement('div');
  comboRow.className = 'cm-chart-view__row cm-chart-view__row--end';
  let windowSeconds = DEFAULT_WINDOW_SECONDS;
  comboRow.append(
    createCombo({
      items: TRACE_INTERVALS.map((entry) => ({ value: String(entry.seconds), label: entry.label })),
      value: String(DEFAULT_WINDOW_SECONDS),
      onChange: (value) => {
        windowSeconds = Number(value);
        redraw.invalidate();
      },
    }),
  );

  const chart = createChartCanvas('cm-trace-canvas');

  const histogramLabel = createLabel('Error Categories:');
  histogramLabel.classList.add('cm-chart-view__label');

  const histogram = createDataTable({ columns: ['Error Category', 'Count'], emptyText: '' });

  view.append(summary, comboRow, chart.element, histogramLabel, histogram.element);
  panel.body.append(view);

  const samples: Sample[] = [];
  const redraw = createRedrawScheduler(drawTrace);

  const resizeObserver = new ResizeObserver(() => redraw.invalidate());
  resizeObserver.observe(chart.element);

  setSummary({
    rxFrameCount: 0,
    busLoadPercent: 0,
    busStatus: {
      stateAvailable: false,
      state: '',
      countersAvailable: false,
      rxErrorCount: 0,
      txErrorCount: 0,
    },
    errorCategoryCounts: {},
  });

  function setSummary(data: BusStatsData): void {
    const state = data.busStatus.stateAvailable ? data.busStatus.state : 'N/A';
    const errors = data.busStatus.countersAvailable
      ? `${data.busStatus.rxErrorCount} / ${data.busStatus.txErrorCount}`
      : 'N/A';
    summaryHead.textContent =
      `Rx Frames: ${data.rxFrameCount}\n` +
      `Bus Load: ${data.busLoadPercent.toFixed(1)}%\n` +
      `Bus State: `;
    summaryState.textContent = state;
    summaryState.style.color = busStateColor(state, data.busStatus.stateAvailable) ?? '';
    summaryTail.textContent = `\nRx/Tx Error Count: ${errors}`;
  }

  function drawTrace(): void {
    chart.sync();
    drawChartFrame(chart);
    const { ctx, width, height, palette } = chart;

    const style = getComputedStyle(chart.element);
    const fontSize = Number.parseFloat(style.fontSize) || 12;
    // Canvas löst keine CSS-Variablen auf — die Familie kommt aufgelöst aus dem Element.
    ctx.font = `${fontSize}px ${style.fontFamily}`;
    ctx.textBaseline = 'middle';

    const labelWidth = ctx.measureText('100%').width;
    const dataLeft = 6 + labelWidth + 6;
    const dataRight = width - 8;
    const dataTop = 8;
    const dataBottom = height - fontSize - 10;
    if (dataRight <= dataLeft || dataBottom <= dataTop) return;

    // Y-Achse fest 0..100 %, vier Intervalle (BusStatsWindow.cpp:428).
    const gridRows = 4;
    ctx.lineWidth = 1;
    for (let row = 0; row <= gridRows; row += 1) {
      const y = dataTop + ((dataBottom - dataTop) * row) / gridRows;
      if (row > 0 && row < gridRows) {
        ctx.strokeStyle = palette.grid;
        ctx.beginPath();
        ctx.moveTo(dataLeft, Math.round(y) + 0.5);
        ctx.lineTo(dataRight, Math.round(y) + 0.5);
        ctx.stroke();
      }
      ctx.fillStyle = palette.textDim;
      ctx.textAlign = 'right';
      ctx.fillText(`${100 - row * (100 / gridRows)}%`, dataLeft - 6, y);
    }

    // X-Achse: "now" bleibt immer, die beiden Altersmarken entfallen bei Platzmangel.
    const axisY = dataBottom + fontSize * 0.7 + 2;
    ctx.fillStyle = palette.textDim;
    ctx.textAlign = 'right';
    ctx.fillText('now', dataRight, axisY);
    const oldLabel = `-${windowSeconds.toFixed(0)}s`;
    const midLabel = `-${(windowSeconds / 2).toFixed(0)}s`;
    const nowWidth = ctx.measureText('now').width;
    const available = dataRight - dataLeft - nowWidth;
    if (available > ctx.measureText(oldLabel).width * 2.5) {
      ctx.textAlign = 'left';
      ctx.fillText(oldLabel, dataLeft, axisY);
      if (available > (ctx.measureText(oldLabel).width + ctx.measureText(midLabel).width) * 2.5) {
        ctx.textAlign = 'center';
        ctx.fillText(midLabel, (dataLeft + dataRight) / 2, axisY);
      }
    }

    if (samples.length < 2) return;

    const now = performance.now();
    const points: Array<{ x: number; y: number; percent: number }> = [];
    for (const sample of samples) {
      const age = (now - sample.t) / 1000;
      if (age > windowSeconds) continue;
      const x = dataRight - (age / windowSeconds) * (dataRight - dataLeft);
      const clamped = sample.percent < 0 ? 0 : sample.percent > 100 ? 100 : sample.percent;
      const y = dataBottom - (clamped / 100) * (dataBottom - dataTop);
      points.push({ x, y, percent: sample.percent });
    }
    if (points.length < 2) return;

    // Jedes Segment bekommt die Farbe seines neueren Endpunkts.
    ctx.lineWidth = 1.6;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    for (let i = 1; i < points.length; i += 1) {
      const a = points[i - 1]!;
      const b = points[i]!;
      ctx.strokeStyle = busLoadColor(b.percent);
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.stroke();
    }
  }

  return {
    panel,
    show(): void {
      panel.show();
      redraw.invalidate();
    },
    refresh(data: BusStatsData): void {
      if (!panel.visible) return;
      setSummary(data);
      const rows = Object.entries(data.errorCategoryCounts)
        .sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0))
        .map(([category, count]) => [category, String(count)]);
      histogram.setRows(rows);
    },
    appendBusLoadSample(percent: number): void {
      const now = performance.now();
      samples.push({ t: now, percent });
      while (samples.length > 0 && (now - samples[0]!.t) / 1000 > SAMPLE_RETENTION_SECONDS) {
        samples.shift();
      }
      if (panel.visible) redraw.invalidate();
    },
    dispose(): void {
      resizeObserver.disconnect();
      redraw.cancel();
      chart.dispose();
    },
  };
}
